import { LockOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { TipEntryCard } from "./TipEntryCard";
import type { BillCloseTip } from "./useBillCloseTip";

const MICRO_LABEL = "text-[11px] font-bold uppercase tracking-[.16em] text-pos-muted";
const SHELL_CLASS =
  "relative flex flex-col gap-2.5 rounded-2xl border border-pos-line bg-pos-card px-4 pb-4 pt-3.5 shadow-[0_1px_2px_rgba(60,40,15,.04)]";

/**
 * Бакшишът в „Закрий" / „Раздели и плати" (дизайнът „Закрий"): скрит зад
 * „+ Добави бакшиш"; натиснат — „Сума дадена от клиента" и клавиатурата
 * (`TipEntryCard`), „Премахни" ги скрива пак.
 * Бакшишът се плаща с метода на сметката — избор на метод няма; бутонът го
 * има само при метод, избран в настройките. Без бакшиш в настройките няма
 * нищо и модалът е старият.
 *
 * Логиката е в `useTipEntry` (кой какво е въвел) и `tipMath` (правилата);
 * тук е само изгледът. Кликовете НЕ стигат до корена на модала — там те
 * нулират трите клика на „В брой" („Закриване на сметка(1)").
 */
export function TipCard({
  closeTip,
  disabled = false,
}: {
  closeTip: BillCloseTip;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const mode = closeTip.cardMode;
  const recorded = closeTip.recorded;
  if (mode === "none" && !recorded) return null;

  // Повторен опит: бакшишът е записан при закриването и не се променя.
  if (recorded) {
    return (
      <div data-testid="recorded-tip" className={SHELL_CLASS}>
        <span className={MICRO_LABEL}>{t("closeTable.tip.title")}:</span>
        <div className="flex items-baseline justify-between gap-2.5">
          <span className="min-w-0 text-[13px] font-semibold text-pos-soft2">
            {t("closeTable.tip.recordedShort", { method: recorded.methodName })}
          </span>
          <span className="flex shrink-0 items-baseline gap-1.5">
            <span className="font-pos-mono text-[18px] font-bold text-pos-accent [font-variant-numeric:tabular-nums]">
              {recorded.amount}
            </span>
            <span className="text-[12px] font-bold text-pos-accent">EUR</span>
          </span>
        </div>
      </div>
    );
  }

  // Заключен, не скрит: без интернет към каса без бакшиш (docs/edge.md) или
  // сметка без нито един ред (BE `tip_requires_items`) — бутонът е сив, а
  // причината е под него.
  if (mode === "locked" || mode === "noItems") {
    return (
      <div data-testid="tip-locked" className="flex flex-col items-start gap-1.5">
        <span className="inline-flex h-10 items-center gap-2 rounded-[10px] border-[1.5px] border-dashed border-pos-line bg-pos-card/50 px-4 text-[14px] font-bold text-pos-muted">
          <LockOutlined className="text-[12px]" />
          {t("closeTable.tip.addTip")}
        </span>
        <span className="text-[12.5px] font-medium leading-[1.45] text-pos-soft2">
          {mode === "noItems"
            ? t("closeTable.tip.errors.tip_requires_items")
            : t("edge.unavailableOffline")}
        </span>
      </div>
    );
  }

  const tip = closeTip.tipEntry;
  if (!closeTip.settings) return null;

  if (!tip.isOpened) {
    return (
      <div data-testid="tip-entry-section" onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          disabled={disabled}
          onClick={tip.openTip}
          className="inline-flex h-10 cursor-pointer items-center gap-2 rounded-[10px] border-[1.5px] border-dashed border-pos-accent/45 bg-pos-card/60 px-4 text-[14px] font-bold text-pos-accent-ink transition-colors hover:border-pos-accent/80 hover:bg-pos-marked disabled:cursor-not-allowed disabled:opacity-50"
        >
          <PlusOutlined className="text-[12px]" />
          {t("closeTable.tip.addTip")}
        </button>
      </div>
    );
  }

  return (
    <TipEntryCard
      tip={tip}
      settings={closeTip.settings}
      disabled={disabled}
    />
  );
}
